import React, { useState } from 'react';

interface EmailLoginFormProps {
  onSuccess: (email: string) => void;
  onBack?: () => void;
  requestOTP: (email: string) => Promise<{ success: boolean; message?: string; error?: string }>;
}

export const EmailLoginForm: React.FC<EmailLoginFormProps> = ({ onSuccess, onBack, requestOTP }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validate email
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      const result = await requestOTP(email.toLowerCase());

      if (result.success) {
        setSuccessMessage(result.message || 'Verification code sent! Check your email.');
        // Wait a moment to show the success message
        setTimeout(() => {
          onSuccess(email.toLowerCase());
        }, 1500);
      } else {
        setError(result.error || 'Failed to send verification code');
      }
    } catch (err) {
      console.error('Request OTP error:', err);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {onBack && (
        <div className="mb-4">
          <button
            onClick={onBack}
            className="flex items-center text-sm text-neutral-medium-gray hover:text-neutral-black dark:hover:text-neutral-white transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
      )}

      <h2 className="text-h2 font-semibold text-center text-neutral-black dark:text-neutral-white mb-2 tracking-subheading">
        Sign in with Email
      </h2>
      <p className="text-center text-body text-neutral-medium-gray dark:text-neutral-medium-gray mb-6">
        We'll send you a verification code to sign in
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-brand-teal/10 border border-brand-teal/30 rounded-lg">
          <p className="text-sm text-brand-teal">{successMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label htmlFor="email" className="block text-sm font-medium text-neutral-black dark:text-neutral-white mb-2">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoFocus
            autoComplete="email"
            className="w-full px-4 py-3 border border-neutral-medium-gray/30 dark:border-neutral-dark-gray rounded-lg bg-neutral-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white focus:ring-2 focus:ring-brand-teal focus:border-transparent disabled:bg-neutral-light-gray dark:disabled:bg-neutral-dark-gray disabled:cursor-not-allowed transition-colors"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !email}
          className="w-full flex items-center justify-center px-4 py-3 bg-brand-teal text-neutral-white font-medium rounded-lg hover:bg-brand-teal-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal disabled:bg-neutral-medium-gray disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
        >
          {loading ? (
            <>
              <img src="/images/logos/loader-circle.svg" alt="Loading..." className="-ml-1 mr-2 h-5 w-5" />
              Sending Code...
            </>
          ) : (
            <>
              Send Verification Code
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </>
          )}
        </button>
      </form>

      <p className="text-center text-xs text-neutral-medium-gray dark:text-neutral-medium-gray mt-6">
        Having trouble? Contact{' '}
        <a href="mailto:support@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover hover:underline">
          support@chathtl.com
        </a>
      </p>
    </div>
  );
};
